import {
  formatDateTimeOrDash,
} from '#/utils/date';
import { formatDocumentCurrencyLabel } from '#/utils/documentCurrencyLabel';
import { formatDisplayAmount, formatDisplayQty } from '#/utils/formatNumber';

import { getStockReceiptSourceUiProfile } from './stockReceiptSource';

function packageQty(pkg) {
  return Number(pkg?.qty ?? pkg?.quantity) || 0;
}

/**
 * 僅當 `amountSummary.totalAmount` 缺席時的預覽 fallback。
 * Search 無金額；Detail／mutation 權威為 `amountSummary.totalAmount`。
 */
function computePreviewTotalAmount(lines = []) {
  return lines.reduce((sum, line) => {
    if (line.amount != null && !Number.isNaN(Number(line.amount))) {
      return sum + Number(line.amount);
    }

    const unitPrice = Number(line.unitPrice) || 0;
    const totalQty = (line.packages ?? []).reduce(
      (qtySum, pkg) => qtySum + packageQty(pkg),
      0,
    );
    return sum + unitPrice * totalQty;
  }, 0);
}

function resolveWarehouseName(warehouse) {
  if (!warehouse) {
    return '-';
  }

  return warehouse.name || warehouse.displayName || '-';
}

function resolveCurrencyCode(detail) {
  if (detail?.currencyCode) {
    return detail.currencyCode;
  }
  if (detail?.sourceType === 'WORK_ORDER') {
    return '';
  }
  return detail?.amountSummary?.currencyCode || '';
}

function formatHeaderChargeLabel(amount, currencyCode) {
  if (amount == null || amount === '') {
    return '—';
  }
  const digits = formatDisplayAmount(amount);
  return currencyCode ? `${currencyCode} ${digits}` : digits;
}

function resolveHeaderTotalAmount(detail) {
  const summaryTotal = detail?.amountSummary?.totalAmount;
  if (summaryTotal != null && summaryTotal !== '') {
    return Number(summaryTotal);
  }
  // 禁以 retired header `amount` 為權威；缺 amountSummary 時改由 line 預覽加總
  return null;
}

export function mapStockReceiptMeta(detail) {
  const lines = detail?.lines ?? [];
  const sourceType = detail?.sourceType || 'MANUAL';
  const sourceProfile = getStockReceiptSourceUiProfile(sourceType);
  const sourceId = detail?.sourceId || null;

  const currencyCode = sourceProfile.showInfoCardCurrency
    ? resolveCurrencyCode(detail)
    : '';
  const currencyLabel = sourceProfile.showInfoCardCurrency
    ? formatDocumentCurrencyLabel({
      currencyCode,
      exchangeRate: detail?.exchangeRate,
    })
    : '';
  const headerTotal = sourceProfile.showInfoCardMoney
    ? resolveHeaderTotalAmount(detail)
    : null;
  const totalAmount = !sourceProfile.showInfoCardMoney
    ? null
    : (headerTotal != null && Number.isFinite(headerTotal)
      ? headerTotal
      : computePreviewTotalAmount(lines));
  const amountNum = totalAmount != null && Number.isFinite(totalAmount)
    ? totalAmount
    : 0;
  const amountDigits = formatDisplayQty(amountNum);
  const totalAmountLabel = currencyCode
    ? `${currencyCode} ${amountDigits}`
    : amountDigits;

  return {
    code: detail?.receiptNo || '-',
    status: detail?.status || '',
    warehouseName: resolveWarehouseName(detail?.warehouse),
    supplierName: detail?.supplier?.name
      || detail?.supplier?.code
      || '-',
    currencyLabel,
    customsDutyLabel: sourceProfile.showInfoCardMoney
      ? formatHeaderChargeLabel(detail?.amountSummary?.customsDutyAmount, currencyCode)
      : '',
    freightLabel: sourceProfile.showInfoCardMoney
      ? formatHeaderChargeLabel(detail?.amountSummary?.freightAmount, currencyCode)
      : '',
    sourceType,
    sourceTypeLabel: sourceProfile.label,
    sourceLockTip: sourceProfile.commercialLockTip,
    showInfoCardMoney: sourceProfile.showInfoCardMoney,
    showInfoCardCurrency: sourceProfile.showInfoCardCurrency,
    showInfoCardSupplier: sourceProfile.showInfoCardSupplier,
    sourceId: sourceId || null,
    createDate: formatDateTimeOrDash(detail?.record?.createdAt),
    reviseDate: formatDateTimeOrDash(detail?.record?.updatedAt),
    closeDate: formatDateTimeOrDash(detail?.history?.completedAt),
    issueReason: detail?.issueReason || '',
    remark: detail?.remark || '',
    totalAmount: amountNum,
    totalAmountLabel,
  };
}
